import { useCallback, useEffect, useState } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../utils/firebase';

/**
 * Loads beers primarily owned by sibling taprooms in the same brewery group.
 * Used for cross-location merge selection in Engage.
 */
function useBreweryGroupSiblingBeers(breweryGroupId, currentBreweryId) {
  const [siblingBeers, setSiblingBeers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    const normalizedGroupId =
      typeof breweryGroupId === 'string' ? breweryGroupId.trim() : '';
    const normalizedCurrentId =
      typeof currentBreweryId === 'string' ? currentBreweryId.trim() : '';

    if (!normalizedGroupId || !normalizedCurrentId) {
      setSiblingBeers([]);
      setLoading(false);
      setError(null);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const breweriesSnap = await getDocs(
        query(
          collection(db, 'breweries'),
          where('breweryGroupId', '==', normalizedGroupId),
        ),
      );

      const siblingIds = breweriesSnap.docs
        .map((docSnap) => docSnap.id)
        .filter((breweryId) => breweryId !== normalizedCurrentId);

      const beersById = new Map();

      await Promise.all(
        siblingIds.map(async (siblingId) => {
          const beersSnap = await getDocs(
            query(
              collection(db, 'beer'),
              where('availableBreweryIds', 'array-contains', siblingId),
            ),
          );

          beersSnap.docs.forEach((beerDoc) => {
            beersById.set(beerDoc.id, {
              id: beerDoc.id,
              ...beerDoc.data(),
            });
          });
        }),
      );

      const rows = Array.from(beersById.values());
      rows.sort((left, right) =>
        (left.name || '').localeCompare(right.name || '', undefined, {
          sensitivity: 'base',
        }),
      );
      setSiblingBeers(rows);
    } catch (err) {
      console.error('Error loading sibling group beers:', err);
      setError('Failed to load sibling taproom beers');
      setSiblingBeers([]);
    } finally {
      setLoading(false);
    }
  }, [breweryGroupId, currentBreweryId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { siblingBeers, loading, error, refresh };
}

export default useBreweryGroupSiblingBeers;
